import { ApiClient, type ApiClientConfig } from './client';
import { createSearchVoters, createSubmitCanvasserAdded } from './endpoints/voters';
import { createAdminEndpoints } from './endpoints/admin';
import { createListAllWalkbooks } from './endpoints/walkbooks';
import { createFinanceEndpoints } from './endpoints/finance';
import { createWorkOrdersEndpoints } from './endpoints/workOrders';
import { createAbortableVoterDataCounts } from './endpoints/voterData';
import { createGeneratedEndpoints } from './generated/endpoints.gen';

// Singleton instance
const apiClient = new ApiClient();

/**
 * Initialize the API client with configuration.
 * Must be called before using getApiClient().
 *
 * @example
 * ```typescript
 * initializeApiClient({
 *   baseURL: 'https://api.example.com',
 *   getAuthToken: () => localStorage.getItem('token'),
 *   onAuthError: () => {
 *     localStorage.removeItem('token');
 *     window.location.href = '/login';
 *   },
 * });
 * ```
 */
export function initializeApiClient(config: ApiClientConfig) {
  apiClient.initialize(config);
}

/**
 * Get the API client with all endpoint methods.
 *
 * @example
 * ```typescript
 * const api = getApiClient();
 *
 * // Auth
 * const user = await api.auth.login({ email, password });
 *
 * // Voters
 * const voters = await api.voters.getVoters({ page: 1, limit: 20 });
 * ```
 */
export function getApiClient() {
  const generated = createGeneratedEndpoints(apiClient);
  return {
    // One entry per controller in WireTypesTests.GeneratedControllers, generated from the API.
    ...generated,
    /** Raw ApiClient instance for custom requests */
    raw: apiClient,
    voters: {
      ...generated.voters,
      searchVoters: createSearchVoters(apiClient),
      submitCanvasserAdded: createSubmitCanvasserAdded(apiClient),
    },
    /** Admin endpoints (volunteer management, demo mode) */
    admin: createAdminEndpoints(apiClient),
    walkbooks: {
      ...generated.walkbooks,
      listAll: createListAllWalkbooks(generated.walkbooks),
    },
    /** Finance capture endpoints (mobile check/receipt photo capture) */
    finance: createFinanceEndpoints(apiClient),
    /** Work Orders endpoints (internal operator surface for vendor text sends) */
    workOrders: createWorkOrdersEndpoints(apiClient),
    voterData: {
      ...generated.voterData,
      ...createAbortableVoterDataCounts(apiClient),
    },
  };
}

// Re-export types and classes
export {
  ApiClient,
  ApiError,
  type ApiClientConfig,
  type LegalStaleDoc,
  LegalAcceptanceRequiredError,
  isLegalWall,
  writesOf,
} from './client';
export * from './endpoints';
export { API_HEADERS } from './headers';
